import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDocumentTypes } from "@/features/compliance/document-types/use-document-types";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveOfflineAcceptanceError } from "./offline-error";
import { RecordOfflineConfirmation } from "./RecordOfflineConfirmation";
import { OFFLINE_NOTE_MAX, OFFLINE_REFERENCE_MAX } from "./types";
import { useRecordOfflineAcceptance } from "./use-offline-acceptances";

interface RecordOfflineAcceptanceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userId: string;
  readonly userName: string;
  /** Types that already have a live acceptance — the API allows one per type. */
  readonly excludedTypeIds: ReadonlySet<string>;
  /** Lets the caller decide where focus goes once the dialog has closed. */
  readonly onCloseAutoFocus?: (event: Event) => void;
}

const SELECT_CLASS = "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

type RecordView = "form" | "confirm";

/**
 * Record that a person signed a document TYPE on paper (ADR-0017 A6). The
 * acceptance satisfies every active version of the type, for every role,
 * until it is revoked — the description says so before anything is sent.
 *
 * Two steps: the form, then a confirmation that restates the person, the
 * document type and the reference (a legal act is never sent straight from
 * the form). The form lives in its own component, mounted only while the
 * dialog is open, so every opening starts blank with no stale error.
 */
export function RecordOfflineAcceptanceDialog({
  open,
  onOpenChange,
  userId,
  userName,
  excludedTypeIds,
  onCloseAutoFocus,
}: RecordOfflineAcceptanceDialogProps) {
  const types = useDocumentTypes();
  const mutation = useRecordOfflineAcceptance(userId);

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="record-offline-dialog" onCloseAutoFocus={onCloseAutoFocus}>
        <RecordForm
          userName={userName}
          types={types}
          excludedTypeIds={excludedTypeIds}
          mutation={mutation}
          onCancel={() => handleOpenChange(false)}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

interface RecordFormProps {
  readonly userName: string;
  readonly types: ReturnType<typeof useDocumentTypes>;
  readonly excludedTypeIds: ReadonlySet<string>;
  readonly mutation: ReturnType<typeof useRecordOfflineAcceptance>;
  readonly onCancel: () => void;
  readonly onDone: () => void;
}

function RecordForm({
  userName,
  types,
  excludedTypeIds,
  mutation,
  onCancel,
  onDone,
}: RecordFormProps) {
  const { t } = useTranslation();
  const [typeId, setTypeId] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<RecordView>("form");

  const available = (types.data ?? []).filter((dt) => !excludedTypeIds.has(dt.id));
  // The chosen type can vanish from the list (e.g. it just got a live
  // acceptance); never submit a selection the user can no longer see.
  const effectiveTypeId = available.some((dt) => dt.id === typeId) ? typeId : "";
  const trimmedReference = reference.trim();
  const trimmedNote = note.trim();
  const canSubmit = effectiveTypeId !== "" && trimmedReference !== "" && !mutation.isPending;

  const selectedType = available.find((dt) => dt.id === effectiveTypeId);

  // The form only moves to the confirmation; the write happens from there.
  const handleContinue = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setView("confirm");
  };

  const handleConfirm = async () => {
    // `canSubmit` covers this, but the latch is spelled out: a second Confirm
    // in the same tick must never send a second legal record.
    if (mutation.isPending) return;
    if (!canSubmit) return;
    try {
      await mutation.mutateAsync({
        documentTypeId: effectiveTypeId,
        reference: trimmedReference,
        ...(trimmedNote === "" ? {} : { note: trimmedNote }),
      });
      onDone();
    } catch (err) {
      // Back on the form: the fix is another type or reference, not a retry.
      setError(resolveOfflineAcceptanceError(err, t));
      setView("form");
    }
  };

  if (view === "confirm" && selectedType) {
    return (
      <RecordOfflineConfirmation
        summary={{
          userName,
          documentTypeName: selectedType.name,
          reference: trimmedReference,
          note: trimmedNote,
        }}
        pending={mutation.isPending}
        onBack={() => setView("form")}
        onConfirm={() => void handleConfirm()}
      />
    );
  }

  return (
    <form onSubmit={handleContinue} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{t("users.tos.recordDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("users.tos.recordDialog.description", { name: userName })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2">
        <Label htmlFor="record-offline-type">{t("users.tos.recordDialog.documentType")}</Label>
        {types.isLoading && (
          <p className="text-xs text-muted-foreground">
            {t("users.tos.recordDialog.loadingTypes")}
          </p>
        )}
        {types.isError && (
          <p data-testid="record-offline-types-error" className="text-sm text-destructive">
            {t("users.tos.recordDialog.typesError")}
          </p>
        )}
        {types.data && available.length === 0 && (
          <p data-testid="record-offline-no-types" className="text-sm text-muted-foreground">
            {t("users.tos.recordDialog.noTypes")}
          </p>
        )}
        <select
          id="record-offline-type"
          data-testid="record-offline-type"
          value={effectiveTypeId}
          onChange={(e) => setTypeId(e.target.value)}
          disabled={mutation.isPending || available.length === 0}
          className={SELECT_CLASS}
        >
          <option value="">{t("users.tos.recordDialog.documentTypePlaceholder")}</option>
          {available.map((dt) => (
            <option key={dt.id} value={dt.id}>
              {dt.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="record-offline-reference">{t("users.tos.recordDialog.reference")}</Label>
        <Input
          id="record-offline-reference"
          data-testid="record-offline-reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          maxLength={OFFLINE_REFERENCE_MAX}
          disabled={mutation.isPending}
          required
          aria-describedby="record-offline-reference-hint"
        />
        <p id="record-offline-reference-hint" className="text-xs text-muted-foreground">
          {t("users.tos.recordDialog.referenceHint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="record-offline-note">{t("users.tos.recordDialog.note")}</Label>
        <Textarea
          id="record-offline-note"
          data-testid="record-offline-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={OFFLINE_NOTE_MAX}
          disabled={mutation.isPending}
        />
      </div>

      {error && (
        <p
          role="alert"
          data-testid="record-offline-error"
          className="text-sm font-medium text-destructive"
        >
          {error}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          {t("users.actions.cancel")}
        </Button>
        <Button type="submit" data-testid="record-offline-submit" disabled={!canSubmit}>
          {t("users.tos.recordDialog.next")}
        </Button>
      </DialogFooter>
    </form>
  );
}
